import React, { useRef, useEffect, MutableRefObject, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useInfiniteQuery } from "@tanstack/react-query";
import { IonSpinner, IonItem, IonLabel, IonList } from "@ionic/react";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";

import SkeletonLoader from "./SkeletonLoader";
import ConversationsSvg from "../theme/images/conversations.svg";
import ContactPlatform from "./common/contact-platform";
import ContactsApi from "../api/contacts";
import useContactsStore from "../store/contacts";
import useContactStore from "../store/contact";
import { Contact } from "../interfaces";

import "./ChatList.css";

const MotionLink = motion(IonItem);

const Item = React.memo<{ contact: Contact; isMulti: boolean }>(
  ({ contact, isMulti }) => {
  const {
    name,
    preview,
    timestamp,
    unread,
    type,
    phoneNumber,
    escalated,
    manual,
    blocked,
    companies,
  } = contact;
  return (
    <>
      <div slot="start" className="profile">
        <ContactPlatform defaultClass="profile-platform" contact={contact} />
      </div>
      <IonLabel className="contact ion-text-nowrap">
        <p className="contact-holder">
          {isMulti &&
            companies?.map((c) => (
              <span className="contact-company" key={c.id}>
                <span className="badge text-bg-light">{c.name}</span>
              </span>
            ))}
          <span className="contact-name fw-semibold-1">
            {name || phoneNumber}
          </span>
        </p>
        <p className="conversation-preview">
          {preview}
        </p>
      </IonLabel>
      <div slot="end" className="details">
        <div className="details-container">
          <div className="half-item timestamp">
            {timestamp}{" "}
            {unread ? (
              <div className="notifications-badge badge color-visito-1-bg">
                {unread}
              </div>
            ) : null}
          </div>
          <div className="half-item">
            {escalated ? (
              <div className="badge text-bg-warning">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div className="fw-semibold-1">
                    <FormattedMessage id="esc.needsReview" />
                  </div>
                </div>
              </div>
            ) : null}
            {manual ? (
              <div className="badge text-bg-info ml-1">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div className="fw-semibold-1">
                    <FormattedMessage id="chats.manual" />
                  </div>
                </div>
              </div>
            ) : null}
            {blocked ? (
              <div className="badge text-bg-danger">
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div className="fw-semibold-1">
                    <FormattedMessage id="chats.blocked" />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
});

interface ILayoutProps {
  select?: (id: string) => void;
  mode: string;
  isMulti: boolean;
  loading?: boolean;
}

const ChatList: React.FC<ILayoutProps> = ({
  select,
  mode,
  isMulti,
  loading = false,
}) => {
  const parentRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const filters = useContactsStore((state) => state.filters);
  const updateContactsDb = useContactsStore((state) => state.updateContactsDb);
  const contactList = useContactsStore((state) => state.contactList);
  const selectedId = useContactStore((state) => state.id);
  const isSearch = useContactsStore((state) => state.searchActive);
  const searchResults = useContactsStore((state) => state.searchResults);
  const isSearching = useContactsStore((state) => state.isSearching);

  const enableInfiniteQuery = !isSearching && !isSearch && (!searchResults || searchResults.length === 0)
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    error,
  } = useInfiniteQuery({
    queryKey: ["getContacts", filters],
    queryFn: ({ pageParam }) =>
      ContactsApi.getContacts({ filter: filters, pageParam }),
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? null,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const canFetchRef = useRef(true);
  const prevResultsCount = useRef(0);

  // In case we want all the contacts data
  // const contactsData: Contact[] = infiniteData?.pages.flatMap(p => p.contacts) ?? [];

  const prevPageRef = useRef<string>("");

  const results = useMemo(() => {
    let arr = isSearch && searchResults && searchResults.length
      ? searchResults
      : isSearch
      ? []
      : contactList;

    // Simple filters
    if (filters.companies.length) {
      arr = arr.filter((c) =>
        c.companies.some((company) => filters.companies.includes(company.id))
      );
    }
    if (filters.simple.length) {
      arr = arr.filter((c) =>
        c?.actionsList?.some((action) => filters.simple.includes(action))
      );
    }
    return arr;
  }, [contactList, filters, searchResults, isSearch]);

  const rowVirtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (mode === "desktop" ? 68 : 67.5),
    overscan: mode === "desktop" ? 8 : 4,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    if (!infiniteData?.pages.length || isFetching || !enableInfiniteQuery) return;

    const lastPage = infiniteData?.pages.at(-1);
    const cursor = lastPage?.nextCursor;
    if (cursor && cursor !== prevPageRef.current) {
      updateContactsDb(lastPage.contacts);
      prevPageRef.current = cursor;
    }
  }, [infiniteData?.pages, isFetching, enableInfiniteQuery]);

  useEffect(() => {
    if (results.length > prevResultsCount.current) {
      canFetchRef.current = true;
    }
  }, [results.length]);

  useEffect(() => {
    if (
      !enableInfiniteQuery ||
      isFetching ||
      !hasNextPage ||
      !canFetchRef.current
    ) {
      return;
    }

    const lastIndex = virtualItems.at(-1)?.index;
    const atBottom = lastIndex === results.length - 1;

    if (atBottom) {
      canFetchRef.current = false;
      prevResultsCount.current = results.length;
      fetchNextPage();
    }
  }, [
    virtualItems.length,
    results?.length,
    hasNextPage,
    isFetching,
    fetchNextPage,
    enableInfiniteQuery
  ]);

  useEffect(() => {
    prevPageRef.current = "";
    canFetchRef.current = true;
    prevResultsCount.current = 0;
  }, [filters]);

  if (loading || (isFetching && results.length === 0) || isSearching) {
    return <SkeletonLoader rows={10} />;
  }

  if (error) {
    return (
      <div className="p-3 color-step-500">
        <FormattedMessage id="chats.errorLoading" defaultMessage="Error loading chats" />
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="empty-list">
        <div className="welcome-content text-center">
          <div className="row justify-content-center">
            <div className="mb-3">
              <img src={ConversationsSvg} alt="" />
            </div>
            <span className="color-step-900 font-14 fw-semibold-1">
              {filters.simple.length ? (
                <FormattedMessage id="chats.emptyStateEscTitle" />
              ) : (
                <FormattedMessage id="chats.emptyStateTitle" />
              )}
            </span>
            <span className="color-step-600 font-12">
              {filters.simple.length ? (
                <FormattedMessage id="chats.emptyStateEscSubtitle" />
              ) : (
                <FormattedMessage id="chats.emptyStateSubtitle" />
              )}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-list-items-holder" ref={parentRef}>
      <div style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
        <IonList
          style={{
            padding: 0,
            margin: 0,
            width: "100%",
            position: "relative",
            height: `${rowVirtualizer.getTotalSize()}px`,
          }}
          className="contact-list"
        >
          <AnimatePresence>
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const { index, start, size } = virtualItem;
              const contact = results[index];
              const { id } = contact;
              const onClick = select ? () => select(id) : undefined;
              return (
                <MotionLink
                  lines="full"
                  className={classNames("list-item", {
                    selected: id === selectedId,
                  })}
                  detail={false}
                  onClick={onClick}
                  key={`${id}`}
                  style={{
                    position: "absolute",
                    left: 0,
                    width: "100%",
                    height: `${size}px`,
                    transform: `translateY(${start}px)`,
                  }}
                  initial={false}
                  animate={{ y: start }}
                  exit={{ opacity: 0 }}
                  transition={{ ease: "linear", duration: 0.3 }}
                >
                  <Item contact={contact} isMulti={isMulti} />
                </MotionLink>
              );
            })}
          </AnimatePresence>
        </IonList>

        {isFetching && (
          <div className="text-center py-2">
            <IonSpinner
              name="dots"
              style={{ width: "38px", height: "38px" }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(ChatList);
